import { ImageDataLike } from 'gatsby-plugin-image';
import { ILeaderImageCardProps } from '../../LeaderImageCard/LeaderImageCard';
import { ILeaderData, GridMatrix } from '../LeaderPageTypes';

export const getImageLocalFile = (leader: ILeaderData): ImageDataLike => {
    return leader.relationships?.field_image?.localFile;
};
export const getLeaderInputs = (
    leader: ILeaderData
): ILeaderImageCardProps => ({
    image: {
        localFile: getImageLocalFile(leader),
        alt: leader.field_image?.alt,
    },
    topLeader: leader.field_top_leadership,
    name: leader.title,
    pageUrl: leader.path?.alias,
    position: leader.field_leadership_position_title,
    secondary: leader.field_secondary_info,
    isLeadershipPage: true,
});

function deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
}

export const findCardRow = (
    arr: GridMatrix = [],
    rowToFind: number
): Array<ILeaderData> => {
    if (rowToFind === 0) return [];

    const searchArr = rowToFind < 0 ? [...arr].reverse() : arr;
    const targetRow = Math.abs(rowToFind);
    let rowCount = 0;

    for (const item of searchArr) {
        if (typeof item !== `string`) {
            rowCount++;
            if (rowCount === targetRow) {
                return item as ILeaderData[];
            }
        }
    }

    return [];
};

/** Sorts the leaders into alternating row counts based on the maxSize variable */
export const createGridMatrix = (
    array: ILeaderData[],
    maxSize: number
): GridMatrix => {
    if (!array.length || maxSize < 1) return [];

    const gridMatrix = [];
    const isAlternating = maxSize >= 3;
    const smallerSize = maxSize - 1;
    let currentIndex = 0;
    let isEvenRow = false;

    while (currentIndex < array.length) {
        const currentSize = isAlternating && isEvenRow ? smallerSize : maxSize;
        const nextIndex = Math.min(currentIndex + currentSize, array.length);

        gridMatrix.push(array.slice(currentIndex, nextIndex));

        currentIndex = nextIndex;
        isEvenRow = !isEvenRow;
    }
    return gridMatrix;
};

/** Adjusts the bottom two rows of the grid so that the grid looks more evenly filled out */
export const adjustGridRows = (
    grid: GridMatrix,
    maxRowCount: number
): GridMatrix => {
    const adjustedGrid = deepClone(grid);
    const secondLastRow = findCardRow(adjustedGrid, -2);
    const lastRow = findCardRow(adjustedGrid, -1);
    if (!secondLastRow?.length || !lastRow?.length) {
        return adjustedGrid;
    }
    if (maxRowCount === 4) {
        if (secondLastRow.length === 4) {
            if (lastRow.length < 3) {
                lastRow.splice(0, 0, secondLastRow.pop());
            }
        } else if (secondLastRow.length === 3) {
            if (lastRow.length === 1) {
                secondLastRow.push(lastRow.pop());
            }
        }
    }
    if (maxRowCount === 3) {
        if (secondLastRow.length === 3) {
            if (lastRow.length === 1) {
                lastRow.splice(0, 0, secondLastRow.pop());
            }
        } else if (secondLastRow.length === 2) {
            if (lastRow.length === 1) {
                secondLastRow.push(lastRow.pop());
            }
        }
    }
    return adjustedGrid;
};

/** Calculates the gap in between cards of a max row based on the width of the row */
export const getCardGap = (
    container: HTMLDivElement,
    maxRowCount: number,
    maxGridWidths: Record<number, number>,
    cardWidth: number
): number => {
    //
    let gap: number;
    if (!container) {
        if (maxRowCount === 4) {
            gap = 44;
        } else if (maxRowCount === 3) {
            gap = 32;
        } else {
            gap = 0;
        }
        return gap;
    }

    // Uses the container's width if less than the max width allowed for a row based on the maxRowCount
    const widthToUse =
        container.clientWidth < maxGridWidths[maxRowCount]
            ? container.clientWidth
            : maxGridWidths[maxRowCount];

    // Calculates the gap between cards in a max row using the following calculation:
    // gap = (container width - cards in max row) / gaps in max row
    gap = (widthToUse - cardWidth * maxRowCount) / (maxRowCount - 1);
    return gap;
};
